/**
 * FarmPacks — background video loading.
 *
 * Horizon's video-background-component attaches every source as soon as it
 * connects, so the homepage's three section videos — around 14 MB between
 * them — start downloading against the hero image and the first tap. None of
 * them is visible until you scroll, and each one sits behind a preview image
 * that has already painted, so nothing is missing while they wait.
 *
 * A video loads when its section is close to the viewport. On a phone it
 * also waits for the page to finish loading first, so the bandwidth goes to
 * the part of the page someone is actually looking at.
 */
(function () {
  if (window.__fpVideoDeferBound) return;
  window.__fpVideoDeferBound = true;

  var SELECTOR = 'video-background-component[data-fp-video-defer]';
  var NARROW = '(max-width: 749px)';
  var MARGIN = 400;

  // The height of the actual window, captured before anything resizes it.
  // Auditing tools stretch the viewport to the full document to take a
  // screenshot, which puts every section "in view" at once and would start
  // all three videos at the end of a run that never scrolled.
  var viewportHeight = window.innerHeight;
  window.addEventListener('orientationchange', function () {
    // A rotation is a real viewport change; re-read it once it has settled.
    window.setTimeout(function () { viewportHeight = window.innerHeight; }, 300);
  });

  /** @param {Element} host */
  function nearViewport(host) {
    var rect = host.getBoundingClientRect();
    return rect.top < viewportHeight + MARGIN && rect.bottom > -MARGIN;
  }

  // Reduced motion hides these videos outright, so there is nothing to fetch.
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function load(host) {
    if (host.dataset.fpVideoLoaded) return;
    host.dataset.fpVideoLoaded = '1';

    var video = host.querySelector('video');
    if (!video) return;

    var sources = video.querySelectorAll('source[data-fp-video-source]');
    if (!sources.length) return;

    for (var i = 0; i < sources.length; i++) {
      sources[i].setAttribute('src', sources[i].getAttribute('data-fp-video-source') || '');
      sources[i].removeAttribute('data-fp-video-source');
    }

    video.preload = 'auto';
    video.load();

    var play = function () {
      var started = video.play();
      // Autoplay can still be refused — a data-saver setting, a policy the
      // browser applies before the muted attribute is seen. The preview image
      // stays put, so the section looks the same either way.
      if (started && started.catch) started.catch(function () {});
    };

    if (video.readyState >= 2) play();
    else video.addEventListener('canplay', play, { once: true });
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (!entries[i].isIntersecting) continue;
              if (!nearViewport(entries[i].target)) continue;
              observer.unobserve(entries[i].target);
              load(entries[i].target);
            }
          },
          // Start the fetch a little before the section arrives, so scrolling
          // onto it still finds the video running rather than starting.
          { rootMargin: MARGIN + 'px 0px' }
        )
      : null;

  function watch(root) {
    if (reducedMotion.matches) return;
    var scope = root && root.querySelectorAll ? root : document;
    var hosts = [].slice.call(scope.querySelectorAll(SELECTOR));
    if (scope !== document && scope.matches && scope.matches(SELECTOR)) hosts.push(scope);

    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].dataset.fpVideoWatched) continue;
      hosts[i].dataset.fpVideoWatched = '1';
      if (observer) observer.observe(hosts[i]);
      else pending.push(hosts[i]);
    }
    if (!observer) sweep();
  }

  /** Fallback for the rare browser with no IntersectionObserver. */
  var pending = [];
  var sweeping = false;

  function sweep() {
    sweeping = false;
    for (var i = pending.length - 1; i >= 0; i--) {
      if (!nearViewport(pending[i])) continue;
      load(pending[i]);
      pending.splice(i, 1);
    }
  }

  if (!('IntersectionObserver' in window)) {
    window.addEventListener(
      'scroll',
      function () {
        if (sweeping) return;
        sweeping = true;
        window.requestAnimationFrame(sweep);
      },
      { passive: true }
    );
  }

  function start() {
    watch(document);

    // Sections morph in from the Theme Editor and from Horizon's own reloads.
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) watch(added[j]);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (window.matchMedia(NARROW).matches && document.readyState !== 'complete') {
    window.addEventListener('load', start, { once: true });
  } else {
    start();
  }
})();
